import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { InvalidateItemPageDataQuery } from '../graphql/graphql';
import { invalidateItemPageData } from '../api/queries';
import { ItemStatusEnum } from '../graphql/types';

const isE2ETest = process.env.NEXT_PUBLIC_E2E === '1';

export const useItemRevalidation = (
  itemId: string,
  remoteUpdatedAt: string,
  isrVersion: string,
  nonSsrCurrentItemDataState: ItemStatusEnum | undefined,
  nonSsrCurrentItemUpdatedAt: string,
  nonSsrCurrentItemRefetch: unknown
) => {
  const { i18n } = useTranslation();
  const hasNoCache =
    !remoteUpdatedAt && nonSsrCurrentItemDataState === ItemStatusEnum.Published;
  const actualUpdatedAt = hasNoCache
    ? nonSsrCurrentItemUpdatedAt
    : remoteUpdatedAt;

  const [invalidateItemPageQuery, { data, loading }] =
    useLazyQuery<InvalidateItemPageDataQuery>(invalidateItemPageData, {
      variables: { id: itemId },
      fetchPolicy: 'network-only'
    });

  const refetch = () => {
    if (hasNoCache) nonSsrCurrentItemRefetch();
    else invalidateItemPageQuery();
  };

  useEffect(() => {
    if (!isE2ETest) {
      const remoteTime = new Date(actualUpdatedAt).getTime();
      const isrTime = new Date(isrVersion).getTime();

      if (remoteTime && remoteTime > isrTime) refetch();
    }
  }, [
    actualUpdatedAt,
    isrVersion,
    itemId,
    i18n.language,
    invalidateItemPageQuery
  ]);

  return {
    isDataRefreshing: loading,
    invalidateItemData: data
  };
};
